import type { AdvisorMode } from "@/lib/api";

export type AdvisorSurface = "home" | "results" | "listing" | "saved_search" | "alert" | "profile_memory" | "general";
export type AdvisorApiSurface = "listing_detail" | "search_results" | "saved_search" | "watch_alert" | "profile_memory" | "general";

export const ADVISOR_MODES: Array<{ mode: AdvisorMode; label: string; prompts: string[] }> = [
  { mode: "refine_search", label: "Refine my search", prompts: ["Is there anything worth seeing this week?", "Am I looking in the right towns?", "Is my budget realistic for these towns?"] },
  { mode: "watch_market", label: "Watch the market", prompts: ["Mae, anything new since I last checked?", "Did anything change with homes I saved?", "Should I be patient or widen the search?"] },
  { mode: "remember_preferences", label: "Remember my preferences", prompts: ["Am I looking in the right towns?", "Should I focus more on Madison?", "Review my search preferences"] },
  { mode: "prepare_next_step", label: "Prepare for next step", prompts: ["What should I ask an agent this week?", "What should I check before touring?", "Draft agent questions"] },
];

export const ADVISOR_PROMPT_GROUPS = [
  {
    title: "Find homes",
    prompts: ["Is there anything worth seeing this week?", "Mae, anything new since I last checked?", "Are there any homes I should probably skip?"],
  },
  {
    title: "Understand tradeoffs",
    prompts: ["Am I looking in the right towns?", "Should I focus more on Madison?", "Is my budget realistic for these towns?", "Should I be patient or widen the search?"],
  },
  {
    title: "Decide what to do",
    prompts: ["Is there anything worth seeing this week?", "Did anything change with homes I saved?", "What should I ask an agent this week?", "What should I check before touring?"],
  },
  {
    title: "Train my search",
    prompts: ["Review my search preferences", "Add a preference", "Help Mae understand what I like"],
  },
  {
    title: "Work with an agent",
    prompts: ["What should I ask the listing agent?", "Prepare questions for an agent", "Draft realtor note"],
  },
];

export const MOBILE_PREFERENCE_PROMPTS = [
  "Add preference",
  "Correct memory",
  "Forget this",
  "More privacy",
  "Lower taxes",
  "Near train",
  "Avoid busy roads",
];

export const ADVISOR_MODE_PROMPTS: Record<AdvisorMode, string[]> = {
  evaluate_home: ["Does this fit my priorities?", "Is this overpriced?", "What are the red flags?", "What should I ask the agent?", "Should I tour this?"],
  refine_search: ["Mae, anything new since I last checked?", "Is there anything worth seeing this week?", "Am I looking in the right towns?", "Should I be patient or widen the search?", "Are there any homes I should probably skip?", "Is my budget realistic for these towns?"],
  watch_market: ["Mae, anything new since I last checked?", "Did anything change with homes I saved?", "Is there anything worth seeing this week?", "Should I be patient or widen the search?"],
  remember_preferences: ["Am I looking in the right towns?", "Should I focus more on Madison?", "Review my search preferences"],
  prepare_next_step: ["What should I ask an agent this week?", "Draft a message to the agent", "What should I check before touring?", "Summarize key unknowns"],
};

export const SAVED_SEARCH_REVIEW_PROMPTS = [
  "Should I save these changes?",
  "Should this be a new search?",
  "What changed from my saved search?",
  "What should I watch for next?",
  "Will this make alerts better?",
  "Is this too broad or too narrow?",
  "Which criteria should I loosen?",
];

export function advisorApiSurface(surface: AdvisorSurface): AdvisorApiSurface {
  if (surface === "listing") return "listing_detail";
  if (surface === "results") return "search_results";
  if (surface === "saved_search") return "saved_search";
  if (surface === "alert") return "watch_alert";
  if (surface === "profile_memory") return "profile_memory";
  return "general";
}

export function inferAdvisorSurface(input: {
  listingId?: number | null;
  savedSearchId?: number | null;
  watchId?: number | null;
  mode?: AdvisorMode | null;
}): AdvisorSurface {
  if (input.listingId) return "listing";
  if (input.savedSearchId || input.watchId || input.mode === "watch_market") return "saved_search";
  if (input.mode === "remember_preferences") return "profile_memory";
  return "general";
}

export function advisorSurfaceLabel(surface: AdvisorSurface) {
  if (surface === "home") return "Home";
  if (surface === "results") return "Results";
  if (surface === "listing") return "Listing";
  if (surface === "saved_search") return "Saved search";
  if (surface === "alert") return "Alert";
  if (surface === "profile_memory") return "Memory cleanup";
  return "General strategy";
}

export function buildAdvisorUrl(options: {
  surface?: AdvisorSurface;
  mode?: AdvisorMode;
  prompt?: string;
  focusAdvisor?: boolean;
  listingId?: number | string | null;
  searchId?: number | string | null;
  savedSearchId?: number | string | null;
  watchId?: number | string | null;
  alertId?: number | string | null;
}) {
  const params = new URLSearchParams();
  if (options.surface) params.set("surface", options.surface);
  if (options.mode) params.set("mode", options.mode);
  if (options.prompt) params.set("prompt", options.prompt);
  if (options.focusAdvisor || options.prompt) params.set("focusAdvisor", "true");
  if (options.listingId) params.set("listing_id", String(options.listingId));
  if (options.searchId) params.set("search_id", String(options.searchId));
  if (options.savedSearchId) params.set("saved_search_id", String(options.savedSearchId));
  if (options.watchId) params.set("watch_id", String(options.watchId));
  if (options.alertId) params.set("alert_id", String(options.alertId));
  const query = params.toString();
  return query ? `/assistant-profile?${query}` : "/assistant-profile";
}

export function promptsForAdvisorSurface(surface: AdvisorSurface, mode: AdvisorMode = "refine_search") {
  if (surface === "saved_search" || surface === "alert") return SAVED_SEARCH_REVIEW_PROMPTS;
  if (surface === "listing") return ADVISOR_MODE_PROMPTS.evaluate_home;
  if (surface === "profile_memory") return ADVISOR_MODE_PROMPTS.remember_preferences;
  return ADVISOR_MODE_PROMPTS[mode] || ADVISOR_MODE_PROMPTS.refine_search;
}

export function buildAlertAdvisorPrompt(input: {
  address?: string | null;
  listingId?: number | string | null;
}) {
  const target = input.address || (input.listingId ? `listing ${input.listingId}` : "this alert");
  return `Why did I get this alert for ${target}? Explain what matched my watch, what missed, what tradeoff exists, what needs verification, and whether it is worth reviewing.`;
}
